// src/services/flowAutomation.service.js
import { api } from './api';

const DRAFT_KEY_PREFIX = 'hrportal_flow_draft_';
const WORKFLOWS_CACHE_KEY = 'hrportal_flow_workflows';

class FlowAutomationService {
    // ─── Workflow CRUD ───────────────────────────────────────────

    async getWorkflows(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.type) params.append('type', filters.type);
            if (filters.status) params.append('status', filters.status);
            if (filters.search) params.append('search', filters.search);
            if (filters.scope) params.append('scope', filters.scope);

            const response = await api.get(`/api/flow-automations?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching workflows:', error);
            return { success: false, workflows: [], error: error.message };
        }
    }

    async getWorkflow(id) {
        try {
            // Check local draft first
            const draft = this.getDraftFromLocal(id);
            if (draft) {
                return { success: true, workflow: draft, source: 'draft' };
            }

            const response = await api.get(`/api/flow-automations/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching workflow:', error);
            const draft = this.getDraftFromLocal(id);
            if (draft) {
                return { success: true, workflow: draft, source: 'draft' };
            }
            return { success: false, workflow: null, error: error.message };
        }
    }

    async createWorkflow(data) {
        try {
            const response = await api.post('/api/flow-automations', data);
            this.clearDraft(data.id);
            return response.data;
        } catch (error) {
            console.error('Error creating workflow, saving draft locally:', error);
            this.saveDraftToLocal(data);
            return { success: true, workflow: data, source: 'draft' };
        }
    }

    async updateWorkflow(id, data) {
        try {
            const response = await api.put(`/api/flow-automations/${id}`, data);
            this.clearDraft(id);
            return response.data;
        } catch (error) {
            console.error('Error updating workflow, saving draft locally:', error);
            this.saveDraftToLocal({ ...data, id });
            return { success: true, workflow: { ...data, id }, source: 'draft' };
        }
    }

    async deleteWorkflow(id) {
        try {
            const response = await api.delete(`/api/flow-automations/${id}`);
            this.clearDraft(id);
            return response.data;
        } catch (error) {
            console.error('Error deleting workflow:', error);
            return { success: false, error: error.message };
        }
    }

    async duplicateWorkflow(id) {
        try {
            const response = await api.post(`/api/flow-automations/${id}/duplicate`);
            return response.data;
        } catch (error) {
            console.error('Error duplicating workflow:', error);
            return { success: false, error: error.message };
        }
    }

    // ─── Workflow Status ─────────────────────────────────────────

    async toggleWorkflowStatus(id, status) {
        try {
            const response = await api.patch(`/api/flow-automations/${id}/status`, { status });
            return response.data;
        } catch (error) {
            console.error('Error toggling workflow status:', error);
            return { success: false, error: error.message };
        }
    }

    async validateWorkflow(id) {
        try {
            const response = await api.post(`/api/flow-automations/${id}/validate`);
            return response.data;
        } catch (error) {
            console.error('Error validating workflow:', error);
            return { success: false, errors: [error.message] };
        }
    }

    // ─── Execution ───────────────────────────────────────────────

    async getExecutions(workflowId, filters = {}) {
        try {
            const params = new URLSearchParams();
            if (workflowId) params.append('workflow_id', workflowId);
            if (filters.status) params.append('status', filters.status);
            if (filters.from) params.append('from', filters.from);
            if (filters.to) params.append('to', filters.to);

            const response = await api.get(`/api/flow-automations/executions?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching executions:', error);
            return { success: false, executions: [], error: error.message };
        }
    }

    async getExecution(executionId) {
        try {
            const response = await api.get(`/api/flow-automations/executions/${executionId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching execution:', error);
            return { success: false, execution: null, error: error.message };
        }
    }

    async triggerManualRun(workflowId, params = {}) {
        try {
            const response = await api.post(`/api/flow-automations/${workflowId}/run`, params);
            return response.data;
        } catch (error) {
            console.error('Error triggering manual run:', error);
            return { success: false, error: error.message };
        }
    }

    async cancelExecution(executionId) {
        try {
            const response = await api.post(`/api/flow-automations/executions/${executionId}/cancel`);
            return response.data;
        } catch (error) {
            console.error('Error cancelling execution:', error);
            return { success: false, error: error.message };
        }
    }

    async retryExecution(executionId) {
        try {
            const response = await api.post(`/api/flow-automations/executions/${executionId}/retry`);
            return response.data;
        } catch (error) {
            console.error('Error retrying execution:', error);
            return { success: false, error: error.message };
        }
    }

    // ─── Approvals ───────────────────────────────────────────────

    async getPendingApprovals() {
        try {
            const response = await api.get('/api/flow-automations/approvals/pending');
            return response.data;
        } catch (error) {
            console.error('Error fetching pending approvals:', error);
            return { success: false, approvals: [], error: error.message };
        }
    }

    async resolveApproval(executionId, approved, comments = '') {
        try {
            const response = await api.post(
                `/api/flow-automations/executions/${executionId}/approve`,
                { approved, comments }
            );
            return response.data;
        } catch (error) {
            console.error('Error resolving approval:', error);
            return {
                success: false,
                error: error.response?.data?.error || error.message,
            };
        }
    }

    // ─── Templates ───────────────────────────────────────────────

    async getTemplates(category) {
        try {
            const params = category ? `?category=${category}` : '';
            const response = await api.get(`/api/flow-automations/templates${params}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching templates:', error);
            return { success: false, templates: this.getBuiltInTemplates(category) };
        }
    }

    async createFromTemplate(templateId, overrides = {}) {
        try {
            const response = await api.post(`/api/flow-automations/templates/${templateId}/use`, overrides);
            return response.data;
        } catch (error) {
            console.error('Error creating from template:', error);
            return { success: false, error: error.message };
        }
    }

    // ─── Analytics ───────────────────────────────────────────────

    async getAutomationStats() {
        try {
            const response = await api.get('/api/flow-automations/stats');
            return response.data;
        } catch (error) {
            console.error('Error fetching automation stats:', error);
            return {
                success: true,
                stats: { active_workflows: 0, total_runs: 0, success_rate: 0, avg_duration_hours: 0 }
            };
        }
    }

    async getWorkflowStats(id) {
        try {
            const response = await api.get(`/api/flow-automations/${id}/stats`);
            return response.data;
        } catch (error) {
            console.error('Error fetching workflow stats:', error);
            return { success: false, error: error.message };
        }
    }

    // ─── Team-Scoped (Manager) ───────────────────────────────────

    async getTeamWorkflows() {
        try {
            const response = await api.get('/api/flow-automations/team');
            return response.data;
        } catch (error) {
            console.error('Error fetching team workflows:', error);
            return { success: false, workflows: [], error: error.message };
        }
    }

    async createTeamWorkflow(data) {
        try {
            const response = await api.post('/api/flow-automations/team', { ...data, scope: 'team' });
            return response.data;
        } catch (error) {
            console.error('Error creating team workflow:', error);
            return { success: false, error: error.message };
        }
    }

    // ─── Local Draft Persistence ─────────────────────────────────

    saveDraftToLocal(workflow) {
        try {
            const key = `${DRAFT_KEY_PREFIX}${workflow.id}`;
            localStorage.setItem(key, JSON.stringify({
                ...workflow,
                _draftSavedAt: new Date().toISOString()
            }));
            return workflow;
        } catch (error) {
            console.error('Error saving draft to localStorage:', error);
            return null;
        }
    }

    getDraftFromLocal(id) {
        try {
            const key = `${DRAFT_KEY_PREFIX}${id}`;
            const data = localStorage.getItem(key);
            return data ? JSON.parse(data) : null;
        } catch (error) {
            console.error('Error reading draft from localStorage:', error);
            return null;
        }
    }

    clearDraft(id) {
        try {
            localStorage.removeItem(`${DRAFT_KEY_PREFIX}${id}`);
        } catch (error) {
            console.error('Error clearing draft:', error);
        }
    }

    getAllLocalDrafts() {
        const drafts = [];
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key?.startsWith(DRAFT_KEY_PREFIX)) {
                    const data = JSON.parse(localStorage.getItem(key));
                    if (data) drafts.push(data);
                }
            }
        } catch (error) {
            console.error('Error reading local drafts:', error);
        }
        return drafts;
    }

    // ─── Built-in Templates (offline fallback) ──────────────────

    getBuiltInTemplates(category) {
        const templates = [
            {
                id: 'tpl_onboarding_basic',
                name: 'Basic Onboarding Flow',
                description: 'Assign onboarding tasks and send welcome notification when a new employee joins',
                category: 'onboarding',
                icon: 'UserCheck',
                steps: [
                    { id: 's1', type: 'trigger', label: 'Employee Added', config: { event: 'employee_added', filters: {} }, position: { x: 250, y: 50 } },
                    { id: 's2', type: 'action', label: 'Send Welcome Email', config: { action_type: 'send_notification', params: { title: 'Welcome!', message: 'Welcome to the team, {{employee.name}}!' } }, position: { x: 250, y: 200 } },
                    { id: 's3', type: 'action', label: 'Assign Onboarding Tasks', config: { action_type: 'create_task', params: { title: 'Complete onboarding checklist', assignee_type: 'trigger_employee' } }, position: { x: 250, y: 350 } },
                    { id: 's4', type: 'end', label: 'End', config: {}, position: { x: 250, y: 500 } },
                ],
                connections: [
                    { id: 'c1', from_step_id: 's1', to_step_id: 's2' },
                    { id: 'c2', from_step_id: 's2', to_step_id: 's3' },
                    { id: 'c3', from_step_id: 's3', to_step_id: 's4' },
                ],
                trigger: { event: 'employee_added', filters: {} },
                popularity: 95,
            },
            {
                id: 'tpl_leave_approval',
                name: 'Multi-Level Leave Approval',
                description: 'Route leave requests through manager and HR approval with auto-escalation',
                category: 'approval',
                icon: 'ClipboardCheck',
                steps: [
                    { id: 's1', type: 'trigger', label: 'Leave Requested', config: { event: 'leave_requested', filters: {} }, position: { x: 250, y: 50 } },
                    { id: 's2', type: 'condition', label: 'Days > 3?', config: { field: 'leave.days', operator: 'greater_than', value: 3 }, position: { x: 250, y: 200 } },
                    { id: 's3', type: 'approval', label: 'Manager Approval', config: { approvers: { type: 'reporting_manager' }, approval_type: 'any', escalate_after_hours: 48 }, position: { x: 100, y: 350 } },
                    { id: 's4', type: 'approval', label: 'HR Approval', config: { approvers: { type: 'role', value: 'HR Admin' }, approval_type: 'any', auto_approve_after_hours: 72 }, position: { x: 100, y: 500 } },
                    { id: 's5', type: 'approval', label: 'Manager Approval Only', config: { approvers: { type: 'reporting_manager' }, approval_type: 'any' }, position: { x: 400, y: 350 } },
                    { id: 's6', type: 'end', label: 'End', config: {}, position: { x: 250, y: 650 } },
                ],
                connections: [
                    { id: 'c1', from_step_id: 's1', to_step_id: 's2' },
                    { id: 'c2', from_step_id: 's2', to_step_id: 's3', label: 'Yes', condition_branch: 'true' },
                    { id: 'c3', from_step_id: 's2', to_step_id: 's5', label: 'No', condition_branch: 'false' },
                    { id: 'c4', from_step_id: 's3', to_step_id: 's4' },
                    { id: 'c5', from_step_id: 's4', to_step_id: 's6' },
                    { id: 'c6', from_step_id: 's5', to_step_id: 's6' },
                ],
                trigger: { event: 'leave_requested', filters: {} },
                popularity: 88,
            },
            {
                id: 'tpl_offboarding',
                name: 'Employee Offboarding',
                description: 'Automate exit tasks including asset return, access revocation, and farewell notification',
                category: 'offboarding',
                icon: 'UserMinus',
                steps: [
                    { id: 's1', type: 'trigger', label: 'Exit Initiated', config: { event: 'employee_exit_initiated', filters: {} }, position: { x: 250, y: 50 } },
                    { id: 's2', type: 'action', label: 'Create Asset Return Task', config: { action_type: 'create_task', params: { title: 'Return company assets', assignee_type: 'trigger_employee' } }, position: { x: 250, y: 200 } },
                    { id: 's3', type: 'action', label: 'Notify IT Team', config: { action_type: 'send_notification', params: { title: 'Access Revocation Required', message: 'Please revoke access for {{employee.name}}' } }, position: { x: 250, y: 350 } },
                    { id: 's4', type: 'approval', label: 'Manager Sign-off', config: { approvers: { type: 'reporting_manager' }, approval_type: 'any' }, position: { x: 250, y: 500 } },
                    { id: 's5', type: 'end', label: 'End', config: {}, position: { x: 250, y: 650 } },
                ],
                connections: [
                    { id: 'c1', from_step_id: 's1', to_step_id: 's2' },
                    { id: 'c2', from_step_id: 's2', to_step_id: 's3' },
                    { id: 'c3', from_step_id: 's3', to_step_id: 's4' },
                    { id: 'c4', from_step_id: 's4', to_step_id: 's5' },
                ],
                trigger: { event: 'employee_exit_initiated', filters: {} },
                popularity: 82,
            },
            {
                id: 'tpl_birthday_wish',
                name: 'Birthday Celebration',
                description: 'Automatically send birthday wishes and notify the team',
                category: 'notification',
                icon: 'Cake',
                steps: [
                    { id: 's1', type: 'trigger', label: 'Birthday Today', config: { event: 'birthday', filters: {} }, position: { x: 250, y: 50 } },
                    { id: 's2', type: 'action', label: 'Send Birthday Wish', config: { action_type: 'send_notification', params: { title: 'Happy Birthday!', message: 'Wishing you a wonderful birthday, {{employee.name}}!' } }, position: { x: 250, y: 200 } },
                    { id: 's3', type: 'end', label: 'End', config: {}, position: { x: 250, y: 350 } },
                ],
                connections: [
                    { id: 'c1', from_step_id: 's1', to_step_id: 's2' },
                    { id: 'c2', from_step_id: 's2', to_step_id: 's3' },
                ],
                trigger: { event: 'birthday', filters: {} },
                popularity: 75,
            },
            {
                id: 'tpl_probation_review',
                name: 'Probation Review Reminder',
                description: 'Remind managers to complete probation reviews before the period ends',
                category: 'task',
                icon: 'AlertCircle',
                steps: [
                    { id: 's1', type: 'trigger', label: 'Probation Ending', config: { event: 'probation_ending', filters: {} }, position: { x: 250, y: 50 } },
                    { id: 's2', type: 'action', label: 'Notify Manager', config: { action_type: 'send_notification', params: { title: 'Probation Review Due', message: '{{employee.name}}\'s probation is ending soon. Please complete the review.' } }, position: { x: 250, y: 200 } },
                    { id: 's3', type: 'action', label: 'Create Review Task', config: { action_type: 'create_task', params: { title: 'Complete probation review for {{employee.name}}', assignee_type: 'reporting_manager', priority: 'high' } }, position: { x: 250, y: 350 } },
                    { id: 's4', type: 'end', label: 'End', config: {}, position: { x: 250, y: 500 } },
                ],
                connections: [
                    { id: 'c1', from_step_id: 's1', to_step_id: 's2' },
                    { id: 'c2', from_step_id: 's2', to_step_id: 's3' },
                    { id: 'c3', from_step_id: 's3', to_step_id: 's4' },
                ],
                trigger: { event: 'probation_ending', filters: {} },
                popularity: 70,
            },
            {
                id: 'tpl_month_end_payroll',
                name: 'Month-end Payroll & Payslips',
                description: 'Automatically process payroll on the last working day of the month, generate payslips and notify employees — with a regularization reminder a week before',
                category: 'payroll',
                icon: 'DollarSign',
                steps: [
                    { id: 's1', type: 'trigger', label: 'Monthly Schedule', config: { event: 'scheduled', filters: {}, schedule: { cron: '0 9 LW * *' } }, position: { x: 250, y: 50 } },
                    { id: 's2', type: 'action', label: 'Process Payroll', config: { action_type: 'process_payroll', params: { period: 'current_month', regularization_reminder: true } }, position: { x: 250, y: 200 } },
                    { id: 's3', type: 'action', label: 'Generate Payslips', config: { action_type: 'generate_payslips', params: { period: 'from_trigger', notify_employees: true } }, position: { x: 250, y: 350 } },
                    { id: 's4', type: 'end', label: 'End', config: {}, position: { x: 250, y: 500 } },
                ],
                connections: [
                    { id: 'c1', from_step_id: 's1', to_step_id: 's2' },
                    { id: 'c2', from_step_id: 's2', to_step_id: 's3' },
                    { id: 'c3', from_step_id: 's3', to_step_id: 's4' },
                ],
                trigger: { event: 'scheduled', filters: {}, schedule: { cron: '0 9 LW * *' } },
                popularity: 88,
            },
            {
                id: 'tpl_auto_payslips',
                name: 'Auto Payslips After Payroll',
                description: 'Whenever payroll is processed (manually or automatically), generate payslips for that month and notify employees',
                category: 'payroll',
                icon: 'Receipt',
                steps: [
                    { id: 's1', type: 'trigger', label: 'Payroll Processed', config: { event: 'payroll_processed', filters: {} }, position: { x: 250, y: 50 } },
                    { id: 's2', type: 'action', label: 'Generate Payslips', config: { action_type: 'generate_payslips', params: { period: 'from_trigger', notify_employees: true } }, position: { x: 250, y: 200 } },
                    { id: 's3', type: 'end', label: 'End', config: {}, position: { x: 250, y: 350 } },
                ],
                connections: [
                    { id: 'c1', from_step_id: 's1', to_step_id: 's2' },
                    { id: 'c2', from_step_id: 's2', to_step_id: 's3' },
                ],
                trigger: { event: 'payroll_processed', filters: {} },
                popularity: 84,
            },
        ];

        if (category) {
            return templates.filter(t => t.category === category);
        }
        return templates;
    }
}

const flowAutomationService = new FlowAutomationService();
export default flowAutomationService;
